// Utilidades de formateo de tiempo para módulo de Engagement
// Centraliza toda la lógica de "Hace Xm/h/d" con colores y fechas exactas

/**
 * Obtiene la clase de color Tailwind según la antigüedad
 * @param {string|Date} date - Fecha a evaluar
 * @returns {string} - Clase de color Tailwind
 * 
 * - Verde: menos de 1 hora
 * - Amarillo: menos de 6 horas
 * - Naranja: menos de 24 horas
 * - Rojo: más de 24 horas
 */
export const getTimeAgoColor = (date) => {
  if (!date) return 'text-slate-500';
  
  const now = new Date();
  const targetDate = new Date(date);
  const diffMs = now - targetDate;
  const diffHours = diffMs / 3600000;

  if (diffHours < 1) return 'text-green-400';
  if (diffHours < 6) return 'text-yellow-400';
  if (diffHours < 24) return 'text-orange-400';
  return 'text-red-400';
};

/**
 * Formatea el tiempo transcurrido como "Hace Xm/h/d"
 * @param {string|Date} date - Fecha a formatear
 * @returns {string} - Texto formateado
 */
export const formatTimeAgo = (date) => {
  if (!date) return 'Nunca';
  
  const now = new Date();
  const targetDate = new Date(date);
  const diffMs = now - targetDate;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Ahora';
  if (diffMins < 60) return `Hace ${diffMins}m`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  return `Hace ${diffDays}d`;
};

/**
 * Formatea fecha/hora en formato corto
 * @param {string|Date} date - Fecha a formatear
 * @returns {string} - Formato: "17/12 14:32" o "ayer 14:32" o "14:32"
 */
export const formatShortDateTime = (date) => {
  if (!date) return '';
  
  const targetDate = new Date(date);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const targetDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());

  const timeStr = targetDate.toLocaleTimeString('es-ES', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });

  // Hoy: solo hora
  if (targetDay.getTime() === today.getTime()) {
    return timeStr;
  }
  
  // Ayer: "ayer HH:MM"
  if (targetDay.getTime() === yesterday.getTime()) {
    return `ayer ${timeStr}`;
  }
  
  // Otro día: "DD/MM HH:MM"
  const dateStr = targetDate.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit'
  });
  return `${dateStr} ${timeStr}`;
};

/**
 * Formatea fecha/hora con segundos para precisión
 * @param {string|Date} date - Fecha a formatear
 * @returns {string} - Formato: "17/12 14:32:45" o "ayer 14:32:45" o "14:32:45"
 */
export const formatTimeWithSeconds = (date) => {
  if (!date) return '';
  
  const targetDate = new Date(date);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const targetDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());

  const timeStr = targetDate.toLocaleTimeString('es-ES', { 
    hour: '2-digit', 
    minute: '2-digit',
    second: '2-digit'
  });

  // Hoy: solo hora con segundos
  if (targetDay.getTime() === today.getTime()) {
    return timeStr;
  }
  
  // Ayer: "ayer HH:MM:SS"
  if (targetDay.getTime() === yesterday.getTime()) {
    return `ayer ${timeStr}`;
  }
  
  // Otro día: "DD/MM HH:MM:SS"
  const dateStr = targetDate.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit'
  });
  return `${dateStr} ${timeStr}`;
};

/**
 * Formatea fecha completa para tooltips o vistas detalladas
 * @param {string|Date} date - Fecha a formatear
 * @returns {string} - Formato: "17/12/2024 14:32"
 */
export const formatFullDateTime = (date) => {
  if (!date) return '';
  
  const targetDate = new Date(date);
  return targetDate.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Componente helper: Retorna objeto con texto y color para usar en JSX
 * @param {string|Date} date - Fecha a evaluar
 * @returns {object} - { timeAgo, shortDate, fullDate, colorClass }
 */
export const getTimeInfo = (date) => {
  return {
    timeAgo: formatTimeAgo(date),
    shortDate: formatShortDateTime(date),
    fullDate: formatFullDateTime(date),
    colorClass: getTimeAgoColor(date),
  };
};

export default {
  getTimeAgoColor,
  formatTimeAgo,
  formatShortDateTime,
  formatTimeWithSeconds,
  formatFullDateTime,
  getTimeInfo,
};